import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close, Person, CheckCircle, Error } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useForgotPasswordByUsernameMutation } from "features/auth/auth";
import CustomTextField from "./common/CustomTextField";
import CustomButton from "./common/CustomButton";

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  initialUsername?: string;
}

type ForgotPasswordInputs = {
  username: string;
};

export default function ForgotPasswordModal({
  open,
  onClose,
  initialUsername = "",
}: ForgotPasswordModalProps) {
  const [forgotPassword, { isLoading }] = useForgotPasswordByUsernameMutation();
  const [resultState, setResultState] = useState<"idle" | "success" | "error">("idle");
  const [resultMessage, setResultMessage] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<ForgotPasswordInputs>();

  useEffect(() => {
    if (open && initialUsername) {
      setValue("username", initialUsername);
    }
  }, [open, initialUsername, setValue]);

  const onSubmit: SubmitHandler<ForgotPasswordInputs> = async (data) => {
    try {
      const response = await forgotPassword({ username: data.username }).unwrap();
      if (response.has_email) {
        setMaskedEmail(response.masked_email || "");
        setResultMessage(response.message);
        setResultState("success");
      } else {
        setResultMessage(response.message);
        setResultState("error");
      }
    } catch (error: any) {
      const message = error?.data?.message || "Something went wrong. Please try again.";
      setResultMessage(message);
      setResultState("error");
    }
  };

  const handleClose = () => {
    setResultState("idle");
    setResultMessage("");
    setMaskedEmail("");
    reset();
    onClose();
  };

  const handleTryAgain = () => {
    setResultState("idle");
    setResultMessage("");
    setMaskedEmail("");
  };

  const showUsernameInput = !initialUsername;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95vw", sm: 480 },
          maxWidth: 480,
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          p: 4,
          outline: "none",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Forgot Password?
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {resultState === "idle" && (
          <>
            <Typography color="text.secondary" mb={3}>
              {showUsernameInput
                ? "Enter your username to reset your password."
                : "Click the button below to receive a password reset link."}
            </Typography>

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              {showUsernameInput && (
                <CustomTextField
                  id="username"
                  label="Username"
                  autoComplete="username"
                  autoFocus
                  error={Boolean(errors.username)}
                  helperText={errors.username?.message}
                  InputProps={{
                    startAdornment: (
                      <Person sx={{ color: "gray", mr: 1 }} />
                    ),
                  }}
                  {...register("username", {
                    required: "Username is required",
                  })}
                />
              )}

              {!showUsernameInput && (
                <Typography color="text.primary" mb={2}>
                  Username: <strong>{initialUsername}</strong>
                </Typography>
              )}

              {isLoading ? (
                <Box display="flex" justifyContent="center" mt={2}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomButton type="submit" sx={{ mt: 2 }}>
                  SEND RESET LINK
                </CustomButton>
              )}

              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mt={2}
              >
                <Typography
                  component="span"
                  sx={{
                    color: "rgb(31, 93, 107)",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={handleClose}
                >
                  ← Back to login
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {resultState === "success" && (
          <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0", width: "100%" }}>
            <Box
              sx={{
                bgcolor: "rgba(76, 175, 80, 0.1)",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />
              <Typography fontWeight="bold" fontSize={14}>
                Password reset link sent
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "white", p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                A password reset link has been sent to
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong style={{ color: "#000" }}>{maskedEmail}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={2}>
                Please check your inbox and follow the instructions to reset your password.
              </Typography>
              <Box display="flex" justifyContent="center" mt={3}>
                <CustomButton
                  variant="outlined"
                  onClick={handleClose}
                  fullWidth={false}
                  sx={{ minWidth: 150 }}
                >
                  CLOSE
                </CustomButton>
              </Box>
            </Box>
          </Box>
        )}

        {resultState === "error" && (
          <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0", width: "100%" }}>
            <Box
              sx={{
                bgcolor: "rgba(211, 47, 47, 0.1)",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Error sx={{ color: "#d32f2f", fontSize: 20 }} />
              <Typography fontWeight="bold" fontSize={14} color="#d32f2f">
                Unable to reset password
              </Typography>
            </Box>
            <Box sx={{ bgcolor: "white", p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {resultMessage}
              </Typography>
              <Box display="flex" justifyContent="center" gap={2} mt={3}>
                <CustomButton
                  variant="outlined"
                  onClick={handleTryAgain}
                  fullWidth={false}
                  sx={{ minWidth: 120 }}
                >
                  TRY AGAIN
                </CustomButton>
                <CustomButton
                  variant="outlined"
                  onClick={handleClose}
                  fullWidth={false}
                  sx={{ minWidth: 120 }}
                >
                  CLOSE
                </CustomButton>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
